import { AllocationShareTooltip } from '@/components/common/allocationShareTooltip'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableRowLink } from '@/components/common/table'
import { ArrowIcon } from '@/components/icons/arrow'
import { routes } from '@/config/routes'
import { useAdvancedAnalyticsCompositionQuery } from '@/queries/advancedAnalytics.query'
import type { Vault } from '@/types/vault'
import type { AdvancedAnalyticsCompositionItem } from '@/utils/internalAppSdk'
import { getProtocolDisplayName } from '@/utils/protocol'
import {
  formatDecimalToPercentage,
  formatTokenValue,
  fromMarketAssetVaultId,
  toMarketId,
} from '@vaultsfyi/common/utils'
import { generatePath } from 'react-router'

interface Props {
  vault: Vault
}

const ALLOCATIONS_SECTION_PROTOCOL_NAMES = ['morpho']
const shortenMarketId = (id: string) => (id.length < 11 ? id : `${id.substring(0, 6)}...${id.substring(id.length - 4)}`)
const getCompositionItemMarket = (item: AdvancedAnalyticsCompositionItem, parentVaultId: Vault['vaultId']) => {
  if (!item.vaultId || item.vaultId === parentVaultId) return undefined

  try {
    const { marketRootAddress, marketProtocolIdentifier, assetAddress } = fromMarketAssetVaultId(item.vaultId)
    const marketId = toMarketId({ marketRootAddress, marketProtocolIdentifier })

    return {
      label: marketProtocolIdentifier,
      path: generatePath(routes.borrowMarket, { network: item.network.name, marketId, assetId: assetAddress }),
    }
  } catch {
    return undefined
  }
}

export const CurrentAllocations = ({ vault }: Props) => {
  const shouldDisplaySection = ALLOCATIONS_SECTION_PROTOCOL_NAMES.includes(vault.protocol.name)
  const { data, isLoading, isError } = useAdvancedAnalyticsCompositionQuery({
    network: vault.network.name,
    vaultId: vault.vaultId,
    enabled: shouldDisplaySection,
  })

  if (!shouldDisplaySection) return null

  const composition = data?.composition ?? []
  const protocolDisplayName = getProtocolDisplayName(vault.protocol)

  return (
    <div className="flex flex-col gap-4">
      <CurrentAllocationsHeader protocolDisplayName={protocolDisplayName} />
      <div className="flex flex-col gap-3">
        <CurrentAllocationsSummary positionCount={composition.length} isError={isError} isLoading={isLoading} />
        <CurrentAllocationsTable composition={composition} vault={vault} isLoading={isLoading} isError={isError} />
      </div>
    </div>
  )
}

const CurrentAllocationsHeader = ({ protocolDisplayName }: { protocolDisplayName: string }) => (
  <div className="flex flex-col gap-2">
    <h4 className="font-medium text-base text-heading-foreground">Current allocations</h4>
    <p className="text-body-foreground text-sm">
      Markets this {protocolDisplayName} vault currently lends into. Allocations are current only and may change as the
      curator reallocates.
    </p>
  </div>
)

const CurrentAllocationsSummary = ({
  positionCount,
  isError,
  isLoading,
}: {
  positionCount: number
  isError: boolean
  isLoading: boolean
}) => (
  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
    <p className="text-body-foreground text-sm">
      Allocated across{' '}
      <span className="text-heading-foreground">
        {isLoading || isError ? '--' : `${positionCount} ${positionCount === 1 ? 'position' : 'positions'}`}
      </span>
    </p>
  </div>
)

const CurrentAllocationsTable = ({
  composition,
  vault,
  isLoading,
  isError,
}: {
  composition: AdvancedAnalyticsCompositionItem[]
  vault: Vault
  isLoading: boolean
  isError: boolean
}) => (
  <div className="overflow-x-auto rounded-2xl border">
    <Table>
      <TableHeader>
        <TableRow variant="header">
          <TableHead>Market</TableHead>
          <TableHead className="text-right">Allocation</TableHead>
          <TableHead className="text-right">Share</TableHead>
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {composition.map((item) => (
          <AllocationRow
            key={`${item.network.networkCaip}:${item.vaultId ?? item.lpToken.tokenCaip ?? item.name}:${item.lpToken.balanceNative}`}
            item={item}
            vault={vault}
          />
        ))}
        {!isLoading && composition.length === 0 && <AllocationsStatusRow isError={isError} />}
        {isLoading && <AllocationsStatusRow isLoading />}
      </TableBody>
    </Table>
  </div>
)

const AllocationsStatusRow = ({ isError = false, isLoading = false }: { isError?: boolean; isLoading?: boolean }) => {
  const message = isLoading
    ? 'Loading current allocations...'
    : isError
      ? 'Current allocations are unavailable.'
      : 'There is no available allocation data for this vault.'

  return (
    <TableRow>
      <TableCell colSpan={4} className="h-20 text-center text-body-foreground text-sm">
        {message}
      </TableCell>
    </TableRow>
  )
}

const AllocationRow = ({
  item,
  vault,
}: {
  item: AdvancedAnalyticsCompositionItem
  vault: Vault
}) => {
  const market = getCompositionItemMarket(item, vault.vaultId)

  return (
    <TableRow className="group">
      <TableCell className="h-14 min-w-40">
        <div className="flex flex-col">
          {market ? (
            <TableRowLink
              to={market.path}
              title={`${item.name} borrow market`}
              prefetch="intent"
              className="w-fit text-heading-foreground underline focus-visible:before:rounded-xl focus-visible:before:ring focus-visible:before:ring-inset"
            >
              {item.name}
            </TableRowLink>
          ) : (
            <span className="text-heading-foreground">{item.name}</span>
          )}
          {market?.label && <span className="text-body-foreground text-xs">{shortenMarketId(market.label)}</span>}
        </div>
      </TableCell>
      <TableCell className="min-w-36 text-right">
        <span className="text-heading-foreground tabular-nums">
          {item.asset
            ? `${formatTokenValue({ value: item.asset.positionValueInAsset, decimals: item.asset.decimals })} ${item.asset.symbol}`
            : '--'}
        </span>
      </TableCell>
      <TableCell className="min-w-28 text-right">
        <div className="ml-auto grid w-fit grid-cols-[1.75rem,3rem] items-center justify-end gap-3">
          {item.allocation !== undefined && item.asset ? (
            <AllocationShareTooltip
              allocation={item.allocation}
              supplyAssets={item.asset.positionValueInAsset}
              assetSymbol={item.asset.symbol}
              assetDecimals={item.asset.decimals}
              marketName={item.name}
            />
          ) : (
            <span />
          )}
          <span className="text-right text-body-foreground tabular-nums">
            {item.allocation === undefined ? '--' : formatDecimalToPercentage(item.allocation, { fractionDigits: 2 })}
          </span>
        </div>
      </TableCell>
      <TableCell className="!pl-1 !pr-3">
        {market && <ArrowIcon className="transition-colors group-hocus:text-accent" />}
      </TableCell>
    </TableRow>
  )
}
